/**
 * Utility functions for handling drag and drop operations with steps
 */

/**
 * Parses a comma-separated string of step IDs into an array (cleaned)
 */
export const parseStepIds = (stepsIds: string | undefined): string[] => {
  if (!stepsIds) return [];

  return stepsIds
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0 && id !== 'undefined' && id !== 'null');
};

/**
 * Validates and cleans a stepsIds string, removing any undefined/empty values
 */
export const cleanStepIds = (stepsIds: string | undefined): string => {
  const cleaned = parseStepIds(stepsIds).join(',');
  return cleaned;
};

/**
 * Safely adds a step ID to a comma-separated string of step IDs (at the end)
 */
export const addStepToIds = (stepsIds: string | undefined, stepId: string): string => {
  if (!stepId || stepId === 'undefined' || stepId === 'null') {
    return cleanStepIds(stepsIds);
  }

  const currentIds = parseStepIds(stepsIds);
  if (currentIds.includes(stepId)) {
    return currentIds.join(',');
  }

  const result = currentIds.length > 0 ? [...currentIds, stepId].join(',') : stepId;
  return result;
};

/**
 * Safely adds a step ID to the beginning of a comma-separated string of step IDs
 */
export const addStepToIdsAtStart = (stepsIds: string | undefined, stepId: string): string => {
  if (!stepId || stepId === 'undefined' || stepId === 'null') {
    return cleanStepIds(stepsIds);
  }

  const currentIds = parseStepIds(stepsIds);
  if (currentIds.includes(stepId)) {
    return currentIds.join(',');
  }

  const result = currentIds.length > 0 ? [stepId, ...currentIds].join(',') : stepId;
  return result;
};

/**
 * Safely removes a step ID from a comma-separated string of step IDs
 */
export const removeStepFromIds = (stepsIds: string | undefined, stepId: string): string => {
  const currentIds = parseStepIds(stepsIds);

  if (!stepId || !stepsIds) {
    return currentIds.join(',');
  }

  const filteredIds = currentIds.filter(id => id !== stepId);
  const result = filteredIds.join(',');
  return result;
};

/**
 * Validates if a step ID exists in a comma-separated string of step IDs
 */
export const isStepInIds = (stepsIds: string | undefined, stepId: string): boolean => {
  const currentIds = parseStepIds(stepsIds);
  return currentIds.includes(stepId);
};

/**
 * Reorders step IDs within a comma-separated string
 */
export const reorderStepIds = (stepsIds: string | undefined, sourceIndex: number, destinationIndex: number): string => {
  const currentIds = parseStepIds(stepsIds);

  // Enhanced validation
  if (sourceIndex < 0 || sourceIndex >= currentIds.length ||
      destinationIndex < 0 || destinationIndex >= currentIds.length ||
      sourceIndex === destinationIndex ||
      currentIds.length === 0) {
    return stepsIds || '';
  }

  try {
    const reorderedIds = [...currentIds];
    const [movedItem] = reorderedIds.splice(sourceIndex, 1);

    // Additional validation that the moved item exists
    if (!movedItem) {
      return stepsIds || '';
    }

    reorderedIds.splice(destinationIndex, 0, movedItem);
    return reorderedIds.join(',');
  } catch (error) {
    return stepsIds || '';
  }
};

/**
 * Determines if a droppable ID is a valid container
 */
export const isValidContainer = (droppableId: string): boolean => {
  if (!droppableId || typeof droppableId !== 'string') {
    return false;
  }
  return droppableId === 'ProcessSidebar' || droppableId.startsWith('Substep---');
};

/**
 * Extracts parent step ID from a substep container ID
 */
export const getParentStepId = (containerId: string): string | null => {
  if (!containerId || typeof containerId !== 'string') {
    return null;
  }

  if (containerId.startsWith('Substep---')) {
    return containerId.replace('Substep---', '');
  }

  return null;
};